import { div } from './format.js';

/* --------------------------------------------------------------------------
   Köps ofta tillsammans

   Korganalys på kvittonivå: ett kvitto är en korg, och frågan är vilka
   artiklar som oftare än vanligt ligger i samma korg som artikel A.

   Två mått, och båda behövs:
   - Andel (confidence): av köpen med A, hur många hade också B.
     n(A och B) / n(A). Det är talet butiksägaren kan använda direkt.
   - Gånger oftare (lift): andelen jämfört med hur ofta B köps över huvud
     taget. Andel / P(B). En bärkasse finns i varannan korg och får hög andel
     med allt — men lift nära 1, för den köps lika ofta utan A. Bara par med
     lift minst MIN_LIFT visas, så att listan säger något om A och inte om B.
     1,2 är en vanlig tumregel: under det är sambandet för svagt att agera på,
     även när det är statistiskt säkert i en stor fil.

   Små tal ger slumpmönster: två kvitton med samma två ovanliga artiklar ger
   en enorm lift. Därför krävs minst MIN_TOGETHER gemensamma kvitton för ett
   par och MIN_RECEIPTS kvitton med A för att A ska gå att välja.

   Varje artikel räknas en gång per kvitto, oavsett antal och rader. Bara
   sålda rader räknas — en retur är inget köp, och kampanjens dolda
   fördelningsrader och kampanjrabatt utan artikel är inga artiklar.

   Korgen är hela kvittot också när urvalet har artikelgrupp eller
   leverantör: "kunder som köpte kaffe köpte också …" ska kunna svara med
   bullar. Ett insnävat kvitto bär originalet i orig (narrowReceipt()).
   Artiklarna att välja, och paren, är däremot de i urvalet: index.selected
   är artiklarna på de insnävade raderna.
   -------------------------------------------------------------------------- */

export const MIN_RECEIPTS = 10;
export const MIN_TOGETHER = 3;
export const MIN_LIFT = 1.2;

/** Artiklarna i ett kvitto, en gång var: Map nyckel -> namn. */
function basketOf(r){
  const out = new Map();
  for(const ln of (r.orig || r).lines){
    if(ln.q > 0 && !ln.promo && !ln.unallocated) out.set(ln.key, ln.name);
  }
  return out;
}

/**
 * Räknar korgarna en gång. Ger {n, count: Map nyckel -> antal kvitton,
 * names: Map nyckel -> namn, baskets: [Set], selected: Set}. Kvitton utan sålda
 * artiklar (bara retur, bara presentkort) är inga korgar och ingår inte i n.
 */
export function basketIndex(receipts){
  const count = new Map(), names = new Map(), baskets = [], selected = new Set();
  for(const r of receipts){
    for(const ln of r.lines) if(ln.q > 0 && !ln.promo && !ln.unallocated) selected.add(ln.key);
    const b = basketOf(r);
    if(!b.size) continue;
    for(const [k, name] of b){
      count.set(k, (count.get(k) || 0) + 1);
      names.set(k, name);   // det senaste namnet gäller, som för kunder
    }
    baskets.push(new Set(b.keys()));
  }
  return {n: baskets.length, count, names, baskets, selected};
}

/** Artiklar i urvalet som finns i tillräckligt många kvitton för att analyseras, flest först. */
export function basketCandidates(index, min = MIN_RECEIPTS){
  return [...index.count].filter(([k, n]) => n >= min && index.selected.has(k))
    .map(([key, n]) => ({key, name: index.names.get(key), receipts: n}))
    .sort((a, b) => b.receipts - a.receipts || a.name.localeCompare(b.name, 'sv'));
}

/**
 * Vad köps tillsammans med artikeln key? Ger null om artikeln har färre än
 * minReceipts kvitton, annars {key, name, receipts, items} där items är
 * [{key, name, together, share, lift}] med share i procent, sorterade efter
 * andel och sedan lift, högst limit stycken.
 */
export function boughtWith(index, key, {limit = 8, minReceipts = MIN_RECEIPTS, minTogether = MIN_TOGETHER, minLift = MIN_LIFT} = {}){
  const nA = index.count.get(key) || 0;
  if(nA < minReceipts) return null;
  const together = new Map();
  for(const b of index.baskets){
    if(!b.has(key)) continue;
    for(const k of b) if(k !== key) together.set(k, (together.get(k) || 0) + 1);
  }
  const items = [];
  for(const [k, nAB] of together){
    if(nAB < minTogether) continue;
    const share = div(nAB, nA);
    const lift = div(share, div(index.count.get(k), index.n));
    if(lift < minLift) continue;
    items.push({key: k, name: index.names.get(k), together: nAB, share: share * 100, lift});
  }
  items.sort((a, b) => b.share - a.share || b.lift - a.lift || a.name.localeCompare(b.name, 'sv'));
  return {key, name: index.names.get(key), receipts: nA, items: items.slice(0, limit)};
}

/**
 * De par som oftast köps tillsammans, med lift minst minLift. Ger
 * [{a, b, aName, bName, together, lift}] sorterade efter antal gemensamma
 * kvitton. Minst en av dem är i urvalet. a är den av de två som köps
 * oftast, så att ett klick visar den artikel vars lista är mest underbyggd —
 * men alltid en artikel i urvalet med minst minReceipts köp, eftersom bara
 * de går att välja.
 */
export function topPairs(index, {limit = 10, minTogether = MIN_TOGETHER, minLift = MIN_LIFT, minReceipts = MIN_RECEIPTS} = {}){
  const pairs = new Map();
  for(const b of index.baskets){
    if(b.size < 2) continue;
    const keys = [...b].sort();
    for(let i = 0; i < keys.length; i++) for(let j = i + 1; j < keys.length; j++){
      const pk = keys[i] + '\u0000' + keys[j];
      pairs.set(pk, (pairs.get(pk) || 0) + 1);
    }
  }
  const out = [];
  for(const [pk, nAB] of pairs){
    if(nAB < minTogether) continue;
    let [a, b] = pk.split('\u0000');
    const inA = index.selected.has(a), inB = index.selected.has(b);
    if(!inA && !inB) continue;
    const na = index.count.get(a), nb = index.count.get(b);
    const lift = div(nAB * index.n, na * nb);
    if(lift < minLift) continue;
    const bFirst = nb > na || (nb === na && index.names.get(b).localeCompare(index.names.get(a), 'sv') < 0);
    if(inA !== inB ? inB : bFirst) [a, b] = [b, a];
    if(index.count.get(a) < minReceipts) continue;   // a måste gå att välja
    out.push({a, b, aName: index.names.get(a), bName: index.names.get(b), together: nAB, lift});
  }
  out.sort((x, y) => y.together - x.together || y.lift - x.lift
    || x.aName.localeCompare(y.aName, 'sv') || x.bName.localeCompare(y.bName, 'sv'));
  return out.slice(0, limit);
}
